import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'

import {
  characterModelUrl,
  QUATERNIUS_MODEL_SCALE,
  resolveCharacterId,
} from '../model/characterCatalog'
import type { AvatarAppearance } from '../model/types'

import type { BuiltAvatar } from './buildAvatarMesh'

const loader = new GLTFLoader()
const templateCache = new Map<
  string,
  { scene: THREE.Group; animations: THREE.AnimationClip[] }
>()

async function loadTemplate(url: string) {
  const hit = templateCache.get(url)
  if (hit) return hit
  const gltf = await loader.loadAsync(url)
  const packed = {
    scene: gltf.scene as THREE.Group,
    animations: gltf.animations ?? [],
  }
  templateCache.set(url, packed)
  return packed
}

function createNameSprite(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 64
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  const draw = (label: string) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = 'rgba(0,0,0,0.6)'
    ctx.beginPath()
    ctx.roundRect(8, 12, 240, 40, 12)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = 'bold 22px system-ui,Segoe UI,sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label.slice(0, 18), 128, 32, 220)
  }
  draw(text)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }),
  )
  sprite.scale.set(0.75, 0.18, 1)
  sprite.position.set(0, 0.42, 0.05)
  sprite.visible = false
  ;(sprite.userData as { redraw: (t: string) => void }).redraw = (t: string) => {
    draw(t)
    tex.needsUpdate = true
  }
  return sprite
}

export type QuaterniusClip = 'Idle' | 'Walk' | 'Run' | 'Punch' | 'Jump'

/**
 * Quaternius glTF avatar for crowd + spotlight.
 */
export async function buildQuaterniusAvatar(
  appearance: AvatarAppearance,
  displayName: string,
  initialClip: QuaterniusClip = 'Walk',
): Promise<BuiltAvatar> {
  const id = resolveCharacterId(appearance.characterId)
  const url = characterModelUrl(id)
  const template = await loadTemplate(url)

  const root = new THREE.Group()
  const body = new THREE.Group()
  root.add(body)

  const cloned = cloneSkinned(template.scene) as THREE.Group
  cloned.rotation.set(0, 0, 0)
  cloned.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.castShadow = false
      obj.receiveShadow = false
      obj.frustumCulled = true
    }
  })

  const scale = QUATERNIUS_MODEL_SCALE * appearance.bodyScale
  body.rotation.set(0, 0, 0)
  body.scale.setScalar(scale)
  body.add(cloned)

  // Plant feet on y=0 so glory scale grows upward (not into the floor)
  body.updateWorldMatrix(true, true)
  const bounds = new THREE.Box3().setFromObject(body)
  if (Number.isFinite(bounds.min.y)) {
    const lift = -bounds.min.y
    // Skinned-mesh bounds can be wild — only apply small corrections
    if (Math.abs(lift) < 0.45) body.position.y += lift
  }
  body.userData.groundY = body.position.y

  const leftArm = new THREE.Group()
  const rightArm = new THREE.Group()
  const leftLeg = new THREE.Mesh(
    new THREE.BoxGeometry(0.01, 0.01, 0.01),
    new THREE.MeshBasicMaterial(),
  )
  const rightLeg = new THREE.Mesh(
    new THREE.BoxGeometry(0.01, 0.01, 0.01),
    new THREE.MeshBasicMaterial(),
  )
  leftLeg.visible = false
  rightLeg.visible = false
  body.add(leftArm, rightArm, leftLeg, rightLeg)

  const head = new THREE.Group()
  // Quaternius characters are ~2 units tall in model space
  head.position.y = 1.85
  body.add(head)

  const mixer = new THREE.AnimationMixer(cloned)
  const actions = new Map<string, THREE.AnimationAction>()
  for (const clip of template.animations) {
    actions.set(clip.name, mixer.clipAction(clip))
  }

  const playLoop = (name: string) => {
    const next =
      actions.get(name) ??
      actions.get('Walk') ??
      actions.get('Idle') ??
      [...actions.values()][0]
    if (!next) return
    mixer.stopAllAction()
    next.reset()
    next.setLoop(THREE.LoopRepeat, Infinity)
    next.clampWhenFinished = false
    next.timeScale = name === 'Walk' || name === 'Run' ? 0.75 : 1
    next.play()
  }

  const playOnce = (name: string) => {
    const next = actions.get(name)
    if (!next) return
    mixer.stopAllAction()
    next.reset()
    next.setLoop(THREE.LoopOnce, 1)
    next.clampWhenFinished = true
    next.play()
  }

  playLoop(initialClip)

  root.userData.mixer = mixer
  root.userData.playClip = playOnce
  root.userData.playIdle = () => playLoop('Idle')
  root.userData.playWalk = () => playLoop('Walk')

  const nameSprite = createNameSprite(displayName)
  // Body-local: above head (body.scale applies). Avoid skinned-mesh bbox — it drifts.
  nameSprite.position.set(0, 4.5, 0.08)
  nameSprite.scale.set(1.1 / Math.max(0.01, scale), 0.28 / Math.max(0.01, scale), 1)
  body.add(nameSprite)

  const dispose = () => {
    mixer.stopAllAction()
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Sprite)) return
      const m = obj.material
      if (m.map) m.map.dispose()
      m.dispose()
    })
  }

  return {
    root,
    body,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    head,
    nameSprite,
    quality: 'high',
    setNameVisible: (v) => {
      nameSprite.visible = v
    },
    updateName: (name) => {
      ;(nameSprite.userData as { redraw: (t: string) => void }).redraw(name)
    },
    dispose,
  }
}

export function tickQuaterniusMixer(root: THREE.Object3D, dt: number): void {
  const mixer = root.userData.mixer as THREE.AnimationMixer | undefined
  mixer?.update(dt)
}

export function playQuaterniusClip(root: THREE.Object3D, name: string): void {
  const play = root.userData.playClip as ((n: string) => void) | undefined
  play?.(name)
}

export function playQuaterniusIdle(root: THREE.Object3D): void {
  const play = root.userData.playIdle as (() => void) | undefined
  play?.()
}

export function playQuaterniusWalk(root: THREE.Object3D): void {
  const play = root.userData.playWalk as (() => void) | undefined
  play?.()
}
